import {
    Card,
    CardBody,
    Stack,
    Heading,
    Text,
    Button,
    useDisclosure
} from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useAlertService } from '@/shared/lib/useAlertService'
import { ParentsService } from '@/shared/api/rest/services/parents.service'
import ConfirmRole from './ConfirmRole'

type RoleRequestCardProps = {
    title: string
    description: string
    icon: React.ReactElement
    /** Which role to request — drives the REST endpoint. */
    role: 'parent' | 'teacher'
    onSuccess?: () => void
}

export default function RoleRequestCard({
    title,
    description,
    icon,
    role,
    onSuccess
}: RoleRequestCardProps) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const alertService = useAlertService()

    const { mutateAsync: requestRole, isPending: loading } = useMutation({
        mutationFn: () =>
            role === 'teacher'
                ? ParentsService.requestTeacherRole()
                : ParentsService.requestParentRole(),
        onSuccess: () => {
            alertService.success(`Your request for ${title} role has been submitted.`)
            onClose()
            onSuccess?.()
        },
        onError: (error: Error) => {
            alertService.error(error.message)
            onClose()
        }
    })

    return (
        <>
            <Card
                variant="outline"
                _hover={{
                    bg: 'gray.50',
                    transform: 'scale(1.01)'
                }}
            >
                <CardBody>
                    <Stack spacing={4} align="flex-start">
                        <Stack direction="row" spacing={4} align="center">
                            {icon}
                            <Heading size="md">{title}</Heading>
                        </Stack>
                        <Text>{description}</Text>

                        <Button
                            width={'full'}
                            variant={'outline'}
                            isLoading={loading}
                            onClick={() => onOpen()}
                        >
                            Request {title} Role
                        </Button>
                    </Stack>
                </CardBody>
            </Card>
            <ConfirmRole
                handleConfirm={requestRole}
                loading={loading}
                isOpen={isOpen}
                onClose={onClose}
            />
        </>
    )
}
